import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox, MapPin, Clock, Package, Wrench, BellRing } from "lucide-react";
import { api, errMsg, fmtWindow, SERVICES } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { pushPermission } from "../../lib/push";
import { Btn, Card, Empty, ServiceIcon, Money, Select, Input } from "../../components/kit";

function Countdown({ to }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const s = Math.max(0, Math.floor((new Date(to) - now) / 1000));
  return <span className={`font-mono text-xs ${s < 30 ? "text-red-400" : "text-neutral-400"}`} data-testid="offer-countdown">{Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}</span>;
}

function OfferCard({ o, onDone }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const act = async (kind) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/offers/${o.offer.id}/${kind}`);
      if (kind === "accept") {
        toast.success("Job assigned to you");
        nav(`/s/jobs/${data.job_id}`);
      } else onDone();
    } catch (e) {
      toast.error(errMsg(e));
      onDone();
    } finally {
      setBusy("");
    }
  };
  return (
    <Card className="space-y-3 fade-in" data-testid="offer-card">
      <div className="flex items-start gap-3">
        <ServiceIcon k={o.service_key} />
        <div className="flex-1 min-w-0">
          <div className="font-head text-xl font-semibold uppercase leading-tight">{o.service_name}</div>
          <div className="text-sm text-neutral-400">{o.vehicle_label}{o.vehicle?.engine && ` · ${o.vehicle.engine}`}</div>
        </div>
        <div className="text-right">
          <Money v={o.payout} green className="text-2xl font-semibold" testid="offer-payout" />
          <div><Countdown to={o.offer.expires_at} /></div>
        </div>
      </div>
      <div className="grid gap-1.5 text-sm text-neutral-300">
        <div className="flex gap-2"><MapPin className="w-4 h-4 text-neutral-500 mt-0.5" />{o.area.city} {o.area.zip} · ~{o.offer.distance_miles} mi <span className="text-neutral-500">(approx. straight-line)</span></div>
        <div className="flex gap-2"><Clock className="w-4 h-4 text-neutral-500 mt-0.5" />{fmtWindow(o.window_start, o.window_end)}</div>
        <div className="flex gap-2"><Package className="w-4 h-4 text-neutral-500 mt-0.5" />{o.parts_responsibility === "driven" ? "Driven supplies parts" : o.parts_responsibility === "customer" ? "Customer-supplied approved parts" : "No parts"}{o.parts_ready && ` · ${o.parts_ready}`}</div>
        {(o.required_skills || o.required_tools) && <div className="flex gap-2"><Wrench className="w-4 h-4 text-neutral-500 mt-0.5" />{[o.required_skills, o.required_tools].filter(Boolean).join(" · ")}</div>}
      </div>
      {o.description && <p className="text-sm text-neutral-500">{o.description}</p>}
      <div className="grid grid-cols-3 gap-2">
        <Btn variant="outline" size="lg" onClick={() => act("decline")} loading={busy === "decline"} data-testid="offer-decline-btn">Decline</Btn>
        <Btn size="lg" className="col-span-2" onClick={() => act("accept")} loading={busy === "accept"} data-testid="offer-accept-btn">Accept</Btn>
      </div>
      <p className="text-[11px] text-neutral-600">Accepting commits you to this appointment window.</p>
    </Card>
  );
}

export default function Offers() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [offers, setOffers] = useState(null);
  const [f, setF] = useState({ service: "", area: "", time: "", min: "" });
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/offers");
      setOffers(data);
    } catch {
      /* retry on next poll */
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);
  const list = useMemo(() => {
    const day = (d) => new Date(d).toDateString();
    const today = new Date();
    const tomorrow = new Date(Date.now() + 864e5);
    return (offers || []).filter((o) =>
      (!f.service || o.service_key === f.service) &&
      (!f.area || `${o.area.city} ${o.area.zip}`.toLowerCase().includes(f.area.toLowerCase())) &&
      (!f.min || o.payout >= Number(f.min)) &&
      (!f.time || (f.time === "today" ? day(o.window_start) === today.toDateString() : f.time === "tomorrow" ? day(o.window_start) === tomorrow.toDateString() : new Date(o.window_start) - today < 7 * 864e5)));
  }, [offers, f]);
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-head text-3xl uppercase font-bold">Offers</h1>
          <p className="text-xs text-neutral-500">First valid acceptance gets the job.</p>
        </div>
      </div>
      {!user.available && <Card className="text-sm text-neutral-400 border-dashed" data-testid="offline-notice">You're Offline. Tap <span className="text-white">Offline</span> at the top to go Available and receive new offers.</Card>}
      {pushPermission() !== "granted" && (
        <button onClick={() => nav("/s/profile")} className="w-full text-left text-sm rounded-2xl border border-[#262626] p-3 flex gap-2 text-neutral-400" data-testid="push-setup-prompt">
          <BellRing className="w-4 h-4 text-[#FF6B00] mt-0.5" />Turn on push alerts in Profile. Offers always appear here even if push is unavailable.
        </button>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Select value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="filter-service">
          <option value="">All services</option>
          {SERVICES.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </Select>
        <Select value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="filter-time">
          <option value="">Any time</option><option value="today">Today</option><option value="tomorrow">Tomorrow</option><option value="week">Next 7 days</option>
        </Select>
        <Input placeholder="City or ZIP" value={f.area} onChange={(e) => setF({ ...f, area: e.target.value })} data-testid="filter-area" />
        <Input placeholder="Min payout $" inputMode="decimal" value={f.min} onChange={(e) => setF({ ...f, min: e.target.value })} data-testid="filter-min-payout" />
      </div>
      {offers === null && <div className="h-40 animate-pulse bg-[#141414] rounded-2xl" />}
      {offers && !list.length && <Empty icon={Inbox} title="No offers right now" text="New nearby jobs appear here automatically." />}
      {list.map((o) => <OfferCard key={o.offer.id} o={o} onDone={load} />)}
    </div>
  );
}
